import { useMemo, useState, useId } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ClipboardCheck,
  FileText,
  ShieldPlus,
  Handshake,
  Cookie,
  Scale,
  ShieldAlert,
  TrendingUp,
} from "lucide-react";
import { scoreColor } from "./gapAssessmentTheme";

const STAGES = [
  { id: "assess", label: "Discover & Assess", short: "Assess", icon: ClipboardCheck, route: "/user/gap-assessment", color: "#6366f1" },
  { id: "ropa", label: "Map Processing", short: "ROPA", icon: FileText, route: "/user/ropa", color: "#2563eb" },
  { id: "pia", label: "Impact Assessment", short: "PIA", icon: ShieldPlus, route: "/user/pia", color: "#818cf8" },
  { id: "tprm", label: "Vendor Risk", short: "TPRM", icon: Handshake, route: "/user/tprm", color: "#0ea5e9" },
  { id: "consent", label: "Consent & Notice", short: "Consent", icon: Cookie, route: null, color: "#d97706" },
  { id: "dsar", label: "Data Subject Rights", short: "DSAR", icon: Scale, route: null, color: "#059669" },
  { id: "breach", label: "Breach Readiness", short: "Breach", icon: ShieldAlert, route: "/user/breach-management", color: "#dc2626" },
  { id: "monitor", label: "Monitor & Report", short: "Report", icon: TrendingUp, route: "/user/reports", color: "#7c3aed" },
];

function stageStatus(stageId, progress) {
  const p = progress?.[stageId];
  if (p == null) return "pending";
  if (p >= 100) return "complete";
  if (p > 0) return "in_progress";
  return "pending";
}

const CX = 200;
const CY = 200;
const ORBIT = 142;
const N = STAGES.length;

function polar(r, a) {
  return { x: CX + r * Math.cos(a), y: CY + r * Math.sin(a) };
}

export default function ComplianceLifecycleWheel({
  complianceScore,
  complianceLabel,
  progress = {},
  onStageClick,
  loading = false,
  highlightedId = null,
  onHoverStage,
  fillColumn = false,
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const uid = useId().replace(/:/g, "");

  const nodes = useMemo(
    () =>
      STAGES.map((stage, i) => {
        const angle = (i / N) * 2 * Math.PI - Math.PI / 2;
        const pos = polar(ORBIT, angle);
        const pct = progress?.[stage.id] ?? 0;
        return { ...stage, ...pos, angle, pct, status: stageStatus(stage.id, progress), index: i };
      }),
    [progress],
  );

  const handleClick = (stage) => {
    if (stage.route) navigate(stage.route);
    else onStageClick?.(stage.id);
  };

  const hot = highlightedId || hovered;
  const scoreDisplay = complianceScore != null ? `${Math.round(complianceScore)}%` : "—";
  const scoreClr = complianceScore != null ? scoreColor(complianceScore) : "#64748b";
  const scorePct = complianceScore != null ? Math.min(100, Math.max(0, complianceScore)) : 0;
  const mainRing = 2 * Math.PI * 118;
  const mainDash = (scorePct / 100) * mainRing;

  return (
    <div
      className={
        fillColumn
          ? "relative w-full aspect-square max-w-full flex-shrink-0"
          : "relative w-full max-w-[300px] sm:max-w-[340px] lg:max-w-[360px] xl:max-w-[380px] mx-auto flex-shrink-0"
      }
    >
      {loading ? (
        <div className={`rounded-full bg-slate-100 animate-pulse border border-slate-200 ${fillColumn ? "absolute inset-0" : "aspect-square w-full"}`} />
      ) : (
        <div className={fillColumn ? "absolute inset-0" : "relative w-full aspect-square"}>
          {/* Ambient glow */}
          <div
            className="absolute inset-[10%] rounded-full pointer-events-none opacity-60"
            style={{
              background: "radial-gradient(circle, rgba(99,102,241,0.14) 0%, rgba(14,165,233,0.06) 45%, transparent 72%)",
            }}
          />

          <svg viewBox="0 0 400 400" className="w-full h-full block relative z-10" role="img" aria-label="DPDP compliance lifecycle wheel">
            <defs>
              <linearGradient id={`${uid}-ring`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a5b4fc" />
                <stop offset="50%" stopColor="#7dd3fc" />
                <stop offset="100%" stopColor="#6ee7b7" />
              </linearGradient>
              <radialGradient id={`${uid}-hub`} cx="35%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#eef2ff" />
              </radialGradient>
              <filter id={`${uid}-glow`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Decorative rings */}
            <circle cx={CX} cy={CY} r={168} fill="#fafbff" stroke="#e0e7ff" strokeWidth="1" />
            <circle cx={CX} cy={CY} r={ORBIT} fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="3 6" />
            <circle cx={CX} cy={CY} r={ORBIT - 18} fill="none" stroke="#f1f5f9" strokeWidth="1" />

            {/* Segment connectors on orbit */}
            {nodes.map((node, i) => {
              const next = nodes[(i + 1) % N];
              const lit = node.status === "complete";
              return (
                <line
                  key={`link-${node.id}`}
                  x1={node.x}
                  y1={node.y}
                  x2={next.x}
                  y2={next.y}
                  stroke={lit ? "#34d399" : "#e2e8f0"}
                  strokeWidth={lit ? 2.5 : 1}
                  strokeLinecap="round"
                  opacity={lit ? 0.85 : 0.5}
                />
              );
            })}

            {/* Main score ring */}
            <circle cx={CX} cy={CY} r={118} fill="none" stroke="#eef2ff" strokeWidth="10" />
            <circle
              cx={CX}
              cy={CY}
              r={118}
              fill="none"
              stroke={`url(#${uid}-ring)`}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${mainDash} ${mainRing}`}
              transform={`rotate(-90 ${CX} ${CY})`}
              filter={`url(#${uid}-glow)`}
            />

            {/* Hub */}
            <circle cx={CX} cy={CY} r={96} fill={`url(#${uid}-hub)`} stroke="#c7d2fe" strokeWidth="1.5" />
            <circle cx={CX} cy={CY} r={88} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
            <text x={CX} y={CY - 14} textAnchor="middle" fill="#6366f1" style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.15em" }}>
              DPDP
            </text>
            <text x={CX} y={CY + 12} textAnchor="middle" fill={scoreClr} style={{ fontSize: 26, fontWeight: 700, fontFamily: "Sora,sans-serif" }}>
              {scoreDisplay}
            </text>
            {complianceLabel && (
              <text x={CX} y={CY + 30} textAnchor="middle" fill={scoreClr} opacity="0.88" style={{ fontSize: 8, fontWeight: 600 }}>
                {complianceLabel.length > 16 ? complianceLabel.slice(0, 14) + "…" : complianceLabel}
              </text>
            )}

            {/* Module nodes */}
            {nodes.map((node) => {
              const Icon = node.icon;
              const isHot = hot === node.id;
              const done = node.status === "complete";
              const active = node.status === "in_progress";
              const r = isHot ? 26 : 23;
              const ringR = r + 5;
              const ringC = 2 * Math.PI * ringR;
              const ringDash = ((node.pct ?? 0) / 100) * ringC;
              const stroke = done ? "#10b981" : active ? node.color : "#cbd5e1";

              return (
                <g
                  key={node.id}
                  className="cursor-pointer"
                  onClick={() => handleClick(node)}
                  onMouseEnter={() => {
                    setHovered(node.id);
                    onHoverStage?.(node.id);
                  }}
                  onMouseLeave={() => {
                    setHovered(null);
                    onHoverStage?.(null);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClick(node);
                    }
                  }}
                >
                  {(node.pct ?? 0) > 0 && (
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={ringR}
                      fill="none"
                      stroke={node.color}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeDasharray={`${ringDash} ${ringC}`}
                      transform={`rotate(-90 ${node.x} ${node.y})`}
                      opacity={done ? 1 : 0.9}
                    />
                  )}
                  <motion.circle
                    cx={node.x}
                    cy={node.y}
                    r={r}
                    fill="white"
                    stroke={isHot ? node.color : stroke}
                    strokeWidth={isHot ? 3 : 2.5}
                    animate={{ scale: isHot ? 1.12 : 1 }}
                    style={{
                      transformOrigin: `${node.x}px ${node.y}px`,
                      filter: isHot ? `drop-shadow(0 6px 14px ${node.color}45)` : "drop-shadow(0 3px 8px rgba(15,23,42,0.08))",
                    }}
                  />
                  {done && <circle cx={node.x} cy={node.y} r={r - 4} fill="#ecfdf5" pointerEvents="none" />}
                  {active && <circle cx={node.x} cy={node.y} r={r - 4} fill="#eff6ff" pointerEvents="none" opacity={0.7} />}
                  <foreignObject x={node.x - 12} y={node.y - 12} width="24" height="24">
                    <div className="flex items-center justify-center w-full h-full pointer-events-none">
                      <Icon size={15} strokeWidth={2.25} style={{ color: done || active ? node.color : "#94a3b8" }} />
                    </div>
                  </foreignObject>
                  <text
                    x={node.x}
                    y={node.y + r + 14}
                    textAnchor="middle"
                    fill={done || active ? "#334155" : "#64748b"}
                    style={{ fontSize: 9, fontWeight: 700 }}
                  >
                    {node.short}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}

export { STAGES, stageStatus };
